import ModeBadge from '@/components/console/mode-badge';
import StatusPill, { type Tone } from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime } from '@/lib/format';
import ToolForm, {
  type BotApp,
  type BotCompany,
  type BotTool,
} from '@/pages/admin/bot-actions/components/tool-form';
import {
  admin_bot_action_confirmation_path,
  admin_bot_action_rejection_path,
  admin_bot_actions_path,
} from '@/rails/routes';
import type { BotAction, ProviderMode } from '@/types/platform';
import { Form, router } from '@inertiajs/react';

interface Props {
  actions: BotAction[];
  tools: BotTool[];
  engineMode: ProviderMode;
  companies: BotCompany[];
  apps: BotApp[];
  plans: string[];
  templates: string[];
}

const STATUSES: Record<BotAction['status'], { label: string; tone: Tone }> = {
  proposed: { label: 'Proposed', tone: 'neutral' },
  awaiting_confirmation: { label: 'Needs your confirm', tone: 'warning' },
  completed: { label: 'Completed', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  halted: { label: 'Halted', tone: 'danger' },
  rejected: { label: 'Rejected', tone: 'neutral' },
};

const Index = ({ actions, tools, engineMode, companies, apps, plans, templates }: Props) => {
  const canPlan = engineMode === 'live';

  // An id reads better as the name it stands for.
  const argumentValue = (name: string, value: string): string => {
    if (name === 'company_id') {
      return companies.find((company) => company.id === value)?.name ?? value;
    }
    if (name === 'app_id') return apps.find((app) => app.id === value)?.name ?? value;

    return value;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Account bot"
        description="Ask in a sentence or pick a tool. Anything that needs a person waits here for a confirm."
        badges={<ModeBadge mode={engineMode} />}
      />

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>What the bot can and cannot do</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-inside list-disc space-y-1.5 text-sm">
            <li>
              It has {tools.length} tools:{' '}
              {tools.map((tool, index) => (
                <span key={tool.tool}>
                  {index > 0 ? ', ' : ''}
                  <code className="text-xs">{tool.tool}</code>
                </span>
              ))}
              .
            </li>
            <li>
              It cannot charge a card, send client mail, delete a project, or push an invoice to
              QuickBooks.
            </li>
            <li>Paid plans and client accounts wait for your confirm.</li>
            <li>After two provisioning failures it stops.</li>
          </ul>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>Ask in a sentence</h2>
            </CardTitle>
            <CardDescription>
              The bot turns the sentence into steps with the tools above. Each step follows the same
              rules as one you pick yourself.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form
              action={admin_bot_actions_path()}
              method="post"
              resetOnSuccess
              className="space-y-3"
            >
              {({ processing }) => (
                <>
                  <Label htmlFor="bot-instruction">Instruction</Label>
                  <Textarea
                    id="bot-instruction"
                    name="instruction"
                    rows={4}
                    required
                    disabled={!canPlan}
                    aria-describedby={canPlan ? undefined : 'bot-instruction-note'}
                    placeholder="Set up Boulder Ridge Builders as a client on Studio with a job tickets app."
                  />
                  {canPlan ? null : (
                    <p id="bot-instruction-note" className="text-muted-foreground text-sm">
                      Planning from a sentence needs OpenRouter connected. Pick a tool below.
                    </p>
                  )}
                  <Button type="submit" disabled={!canPlan || processing}>
                    {processing ? 'Planning…' : 'Ask the bot'}
                  </Button>
                </>
              )}
            </Form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <h2>Pick a tool</h2>
            </CardTitle>
            <CardDescription>
              Each tool takes exactly the inputs shown. Nothing else is sent.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ToolForm
              tools={tools}
              companies={companies}
              apps={apps}
              plans={plans}
              templates={templates}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Log</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {actions.length === 0 ? (
            <EmptyState description="The bot has not been asked to do anything yet." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tool</TableHead>
                  <TableHead>Arguments</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Asked by</TableHead>
                  <TableHead>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {actions.map((action) => (
                  <TableRow key={action.id} className="align-top">
                    <TableCell className="align-top">
                      <code className="text-xs">{action.tool}</code>
                      {action.instruction ? (
                        <p className="text-muted-foreground mt-1 max-w-56 text-xs break-words whitespace-normal">
                          From: “{action.instruction}”
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell className="align-top">
                      {action.arguments.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <dl className="space-y-0.5 text-xs">
                          {action.arguments.map((argument) => (
                            <div key={argument.name} className="flex gap-1.5">
                              <dt className="text-muted-foreground">{argument.name}</dt>
                              <dd className="max-w-48 break-words whitespace-normal">
                                {argumentValue(argument.name, argument.value)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      {action.companyName || action.appName ? (
                        <>
                          <div>{action.companyName ?? '—'}</div>
                          {action.appName ? (
                            <div className="text-muted-foreground text-xs">{action.appName}</div>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <StatusPill tone={STATUSES[action.status].tone}>
                        {STATUSES[action.status].label}
                      </StatusPill>
                      {action.error ? (
                        <p className="mt-1 max-w-64 min-w-40 text-xs break-words whitespace-normal text-red-700">
                          {action.error}
                        </p>
                      ) : null}
                      {action.confirmedBy ? (
                        <p className="text-muted-foreground mt-1 text-xs">
                          {action.status === 'rejected' ? 'Rejected' : 'Confirmed'} by{' '}
                          {action.confirmedBy}
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell className="align-top">
                      <div>{action.requestedBy ?? '—'}</div>
                      <div className="text-muted-foreground text-xs">
                        {formatDateTime(action.createdAt)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right align-top">
                      {action.status === 'awaiting_confirmation' ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() =>
                              router.post(admin_bot_action_confirmation_path(action.id))
                            }
                          >
                            Confirm
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => router.post(admin_bot_action_rejection_path(action.id))}
                          >
                            Reject
                          </Button>
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Index;
